import React, { useMemo } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import SiteFooter from "../../general/components/layout/SiteFooter";
import SiteNavbar from "../../general/components/layout/SiteNavbar";
import {
    getCoursePath,
    resolveCourse,
} from "../components/courseRouteUtils";

const ARGENTINA_TRANSFER_DETAILS = [
    {
        label: "Titular",
        value: "Fundacion Iberoamericana de Salud Publica",
    },
    {
        label: "CUIT",
        value: "30-71234567-8",
        mono: true,
    },
    {
        label: "Banco",
        value: "Banco Santander",
    },
    {
        label: "CBU",
        value: "0720012340000012345678",
        mono: true,
    },
    {
        label: "Alias",
        value: "FISP.POSGRADO.NUTRI",
        mono: true,
    },
];

const normalizeText = (value) => {
    if (typeof value !== "string" || !/[\u00C3\u00C2\u00E2]/.test(value)) {
        return value || "";
    }

    try {
        const bytes = Uint8Array.from(value, (char) => char.charCodeAt(0));
        const decoded = new TextDecoder("utf-8").decode(bytes);

        return decoded.includes("\uFFFD") ? value : decoded;
    } catch {
        return value;
    }
};

const getThemeStyle = (curso) => ({
    "--checkout-primary": curso?.theme?.primary || "#1b2a4a",
    "--checkout-primary-dark": curso?.theme?.primaryDark || "#0f1b33",
    "--checkout-primary-light": curso?.theme?.primaryLight || "#5b9bd5",
    "--checkout-pale": curso?.theme?.pale || "#e2e8f0",
    "--checkout-background": curso?.theme?.background || "#f8fafc",
    "--checkout-navy": curso?.theme?.navy || "#1e293b",
    "--checkout-navy-dark": curso?.theme?.navyDark || "#0f172a",
    "--checkout-hero-start": curso?.theme?.heroStart || "#0f172a",
    "--checkout-hero-mid": curso?.theme?.heroMid || "#334155",
    "--checkout-hero-end": curso?.theme?.heroEnd || "#1e293b",
});

export default function InscripcionConfirmacion() {
    const { slug } = useParams();
    const location = useLocation();
    const family = window.location.pathname.split("/")[1];
    const curso = useMemo(() => resolveCourse(family, slug), [family, slug]);

    if (!curso) {
        return <Navigate to="/educativo" replace />;
    }

    const coursePath = getCoursePath(curso);
    const themeStyle = getThemeStyle(curso);
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get("status");
    const method = searchParams.get("method");
    const isTransfer = status === "transfer";
    const isCardRequest = status === "card";
    const isRecurringRequest = method === "recurring";
    const paymentMethod =
        location.state?.paymentMethodLabel || "medio de pago seleccionado";
    const paymentAmount = location.state?.paymentAmount || "";
    const discountCodeApplied = Boolean(location.state?.discountCodeApplied);
    const discountPercent = Number(location.state?.discountPercent || 0);
    const email = location.state?.email || "tu correo electronico";
    const courseName = location.state?.courseName || normalizeText(curso.nombre);
    const transferDetails = Array.isArray(location.state?.transferDetails)
        ? location.state.transferDetails
        : ARGENTINA_TRANSFER_DETAILS;
    const showTransferDetails = isTransfer && transferDetails.length > 0;

    const content = isTransfer
        ? {
              title: "Inscripcion registrada",
              text: showTransferDetails
                  ? `Recibimos tu solicitud para ${courseName}. Revisa ${email} y usa los datos bancarios de abajo para completar el pago.`
                  : `Recibimos tu solicitud para ${courseName}. Revisa ${email} porque ahi te enviaremos los datos para completar el pago.`,
              note: "Una vez realizada la transferencia, responde el mail con tu comprobante para validar la vacante.",
          }
        : isCardRequest
        ? {
              title: "Solicitud registrada",
              text: `Recibimos tu solicitud para ${courseName}. Revisa ${email} porque ahi te vamos a enviar la confirmacion y los proximos pasos para finalizar el pago con tarjeta.`,
              note: `Medio seleccionado: ${paymentMethod}.`,
          }
        : isRecurringRequest
        ? {
              title: "Suscripcion activada",
              text: `La primera cuota de ${courseName} quedo registrada correctamente. Revisa ${email} porque ahi te enviaremos el comprobante y el detalle del plan recurrente.`,
              note:
                  location.state?.recurringSummary ||
                  `Plan activado con ${paymentMethod}.`,
          }
        : {
              title: "Pago confirmado",
              text: `Tu inscripcion quedo registrada correctamente. Revisa ${email} porque ahi te enviaremos el comprobante y los proximos pasos para comenzar.`,
              note: `Pago realizado con ${paymentMethod}.`,
          };

    const statusIcon = isTransfer
        ? "->"
        : isCardRequest
        ? "*"
        : isRecurringRequest
        ? "R"
        : "OK";

    return (
        <div
            className="course-enrollment course-enrollment-success"
            style={themeStyle}
        >
            <SiteNavbar
                activeSection="educativo"
                navTheme={curso.theme}
                checkoutMode
                secureLabel="Inscripcion segura · SSL"
            />

            <main className="enrollment-success-main">
                <div className="enrollment-shell">
                    <section className="enrollment-success-card">
                        <span className="enrollment-success-icon" aria-hidden="true">
                            {statusIcon}
                        </span>
                        <span className="enrollment-success-kicker">
                            Confirmacion · FISP Educativo
                        </span>
                        <h1>{content.title}</h1>
                        <p className="enrollment-success-text">{content.text}</p>
                        <p
                            className={`enrollment-success-note${
                                isTransfer ? " is-plain" : ""
                            }`}
                        >
                            {content.note}
                        </p>

                        {showTransferDetails ? (
                            <div className="enrollment-success-bank">
                                <h2>Datos bancarios</h2>
                                {paymentAmount ? (
                                    <div className="enrollment-success-bank-row enrollment-success-bank-amount">
                                        <span>Monto a transferir</span>
                                        <strong>{paymentAmount}</strong>
                                    </div>
                                ) : null}
                                {discountCodeApplied && discountPercent ? (
                                    <div className="enrollment-success-bank-row">
                                        <span>Descuento aplicado</span>
                                        <strong>{discountPercent}% OFF</strong>
                                    </div>
                                ) : null}
                                <div className="enrollment-success-bank-grid">
                                    {transferDetails.map((item) => (
                                        <div
                                            className="enrollment-success-bank-row"
                                            key={item.label}
                                        >
                                            <span>{item.label}</span>
                                            <strong
                                                className={item.mono ? "is-mono" : ""}
                                            >
                                                {item.value}
                                            </strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="enrollment-success-actions">
                            <Link
                                to={coursePath}
                                className="enrollment-success-primary"
                            >
                                Volver al programa
                            </Link>
                            <Link
                                to="/educativo"
                                className="enrollment-success-secondary"
                            >
                                Ver oferta educativa
                            </Link>
                        </div>
                    </section>
                </div>
            </main>

            <SiteFooter />
        </div>
    );
}
